import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useViewDoc } from '@/composables/useClientViews'
import {
    openingDate,
    resolveLocalized,
    type PilotageOpeningView,
} from '@/types/pilotageOpening'

/**
 * MYAC-1327 — the QMS's own opening message, in the dock.
 *
 * Once the chat is attached to a QMS it IS that QMS's chat — "a second window for
 * the same thing" (Arslene, 2026-09-16): the same conversation the Pilotage tab
 * shows (one chatbot store, so a message sent in either appears in both), opening
 * on the same pre-generated message from `views/pilotage_opening` and its reply
 * chips. READ, never generated here — MYAC-1313's contract is no model call at
 * load.
 *
 * TWO DELIBERATE DIFFERENCES from `PilotageOpeningChat.vue`, both because this
 * host knows less than the page does:
 *
 * 1. **No hardcoded starter.** The page shows a warm "nothing's been started yet"
 *    opening when the project has no dossier — it can, because it has already
 *    loaded the adopted referentials. The dock has not, and claiming "nothing's
 *    been started" about a project that merely hasn't loaded would be a false
 *    statement about the member's own work. Absent view → the chat's generic
 *    welcome, which is never wrong.
 * 2. **QMS track by default.** The dock attaches to a QMS; the `td` track
 *    belongs to a Product's own surface. The welcome letter (MYAC-1522) passes
 *    `td` when the chat's subject is a Product, so a company with technical
 *    documentation only still reads its own opening.
 *
 * Absent doc, unknown shape, empty message or a subscription error all degrade to
 * the generic welcome: a greeting must never block the chat.
 *
 * NOTE: the locale resolution and reply mapping below mirror
 * `PilotageOpeningChat.vue`. That file belongs to MYAC-1313 and was being changed
 * while this landed, so it is deliberately left alone rather than refactored
 * underneath it; fold the two together once that work settles.
 */
export function useScopedOpening(projectId: () => string | null, track: () => 'qms' | 'td' = () => 'qms') {
    const { locale } = useI18n()
    const openingDoc = useViewDoc<PilotageOpeningView>(projectId, 'pilotage_opening')

    return computed(() => {
        if (!projectId()) return null
        // While the view is in flight, hold the place: the generic welcome
        // flashing before the real message reads as a glitch (MYAC-1313 review).
        if (openingDoc.pending.value) {
            return { message: '', replies: [], eventAt: null, pending: true }
        }
        const doc = openingDoc.data.value?.[track()] ?? null
        if (!doc) return null
        const message = resolveLocalized(doc.message, locale.value)
        if (!message.trim()) return null
        return {
            message,
            replies: (doc.replies ?? [])
                // A chip sends exactly what it shows: its label is the user's
                // message, so the view's longer `prompt` is not used.
                .map(r => ({
                    label: resolveLocalized(r.label, locale.value),
                    kind: r.kind ?? 'open',
                }))
                .filter(r => r.label)
                .slice(0, 3),
            eventAt: openingDate(doc.event_at),
        }
    })
}
